import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isCancel } from 'axios'
import { listInvoicePage } from '@/api/invoices'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { FilterChips } from '@/components/FilterChips'
import { PaginationControls } from '@/components/PaginationControls'
import { PeriodMenu } from '@/components/PeriodMenu'
import { SortMenu } from '@/components/SortMenu'
import { InvoiceFilterMenu } from '@/components/invoice/InvoiceFilterMenu'
import { InvoiceTableRow } from '@/components/invoice/InvoiceTableRow'
import { formatCurrency } from '@/lib/format'
import { listInvoiceFacetChips, parseInvoiceFacetFilters, writeInvoiceFacetFilters } from '@/lib/invoiceFilters'
import { AFTER_PARAM, BEFORE_PARAM, clearCursor, parseCursor, writeCursor } from '@/lib/pagination'
import { DEFAULT_SORT, INVOICE_SORT_OPTIONS, parseSort, writeSort } from '@/lib/sort'
import type { InvoiceFacetFilters } from '@/lib/invoiceFilters'
import type { DateRange, FilterChip } from '@/lib/filters'
import type { Invoice, InvoiceListSort, InvoiceListSummary } from '@/api/types'

const PAGE_SIZE = 200
const SEARCH_PARAM = 'search'
const LOADING_MESSAGE = 'Laddar fakturor...'
const ERROR_MESSAGE = 'Kunde inte hämta fakturor just nu.'
const EMPTY_FILTERED_MESSAGE = 'Inga fakturor hittades.'
const EMPTY_MESSAGE = 'Inga fakturor ännu – de skapas automatiskt när leveranser skickas.'
const EMPTY_SUMMARY: InvoiceListSummary = { invoiced: '0', paid: '0', outstanding: '0' }

function buildSearchParams(searchText: string, facets: InvoiceFacetFilters, sort: InvoiceListSort): URLSearchParams {
  const params = new URLSearchParams()
  const search = searchText.trim()

  if (search !== '') {
    params.set(SEARCH_PARAM, search)
  }

  writeInvoiceFacetFilters(params, facets)
  writeSort(params, sort)

  return params
}

function formatInvoiceCount(count: number): string {
  const noun = count === 1 ? 'faktura' : 'fakturor'

  return `${count} ${noun}`
}

interface InvoiceSummaryBarProps {
  count: number
  summary: InvoiceListSummary
}

function InvoiceSummaryBar({ count, summary }: InvoiceSummaryBarProps): React.ReactElement {
  const hasInvoices = count > 0

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-6 py-4">
      <p className="text-sm text-muted-foreground">{formatInvoiceCount(count)}</p>
      {hasInvoices && (
        <p className="text-sm tabular-nums">
          Fakturerat {formatCurrency(Number(summary.invoiced))} · Betalt {formatCurrency(Number(summary.paid))} · Utestående{' '}
          {formatCurrency(Number(summary.outstanding))}
        </p>
      )}
    </div>
  )
}

export function InvoicesPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchText, setSearchText] = useState('')
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [summary, setSummary] = useState<InvoiceListSummary>(EMPTY_SUMMARY)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrevious, setHasPrevious] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const searchParam = searchParams.get(SEARCH_PARAM) ?? ''
  const after = parseCursor(searchParams.get(AFTER_PARAM))
  const before = parseCursor(searchParams.get(BEFORE_PARAM))
  const facetFilters = useMemo(() => parseInvoiceFacetFilters(searchParams), [searchParams])
  const sort = parseSort(searchParams, INVOICE_SORT_OPTIONS, DEFAULT_SORT)

  const firstInvoiceId = invoices.at(0)?.id
  const lastInvoiceId = invoices.at(-1)?.id

  const facetChipSpecs = listInvoiceFacetChips(facetFilters)
  const hasFacetChips = facetChipSpecs.length > 0
  const hasSearch = searchParam !== ''
  const hasActiveFilters = hasSearch || hasFacetChips
  const hasError = errorMessage !== null
  const hasNoInvoices = !isLoading && !hasError && invoices.length === 0
  const hasNextPage = !isLoading && hasNext && lastInvoiceId !== undefined
  const hasPreviousPage = !isLoading && hasPrevious && firstInvoiceId !== undefined
  const showPagination = !hasError && (invoices.length > 0 || after !== undefined || before !== undefined)
  const emptyMessage = hasActiveFilters ? EMPTY_FILTERED_MESSAGE : EMPTY_MESSAGE

  useEffect(() => {
    setSearchText(searchParam)
  }, [searchParam])

  useEffect(() => {
    const controller = new AbortController()

    const loadInvoices = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const result = await listInvoicePage(
          {
            search: searchParam || undefined,
            status: facetFilters.status || undefined,
            from: facetFilters.period.from || undefined,
            to: facetFilters.period.to || undefined,
            sort,
            limit: PAGE_SIZE,
            after,
            before
          },
          controller.signal
        )

        setInvoices(result.invoices)
        setTotalCount(result.total)
        setSummary(result.summary)
        setHasNext(result.has_next)
        setHasPrevious(result.has_previous)
      } catch (error) {
        if (isCancel(error)) return

        setErrorMessage(ERROR_MESSAGE)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    void loadInvoices()

    return () => controller.abort()
  }, [searchParam, facetFilters, sort, after, before])

  const handleSearch = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    setSearchParams(buildSearchParams(searchText, facetFilters, sort))
  }

  const handleReset = (): void => {
    setSearchText('')
    setSearchParams({})
  }

  const handleFacetsChange = (next: InvoiceFacetFilters): void => {
    setSearchParams((previous) => {
      clearCursor(previous)
      writeInvoiceFacetFilters(previous, next)

      return previous
    })
  }

  const handlePeriodChange = (period: DateRange): void => {
    handleFacetsChange({ ...facetFilters, period })
  }

  const handleSortChange = (next: InvoiceListSort): void => {
    setSearchParams((previous) => {
      clearCursor(previous)
      writeSort(previous, next)

      return previous
    })
  }

  const navigateToCursor = (cursorParam: string, invoiceId: number): void => {
    setSearchParams((previous) => {
      writeCursor(previous, cursorParam, invoiceId)

      return previous
    })
  }

  const handlePreviousPage = (): void => {
    if (firstInvoiceId === undefined) {
      return
    }

    navigateToCursor(BEFORE_PARAM, firstInvoiceId)
  }

  const handleNextPage = (): void => {
    if (lastInvoiceId === undefined) {
      return
    }

    navigateToCursor(AFTER_PARAM, lastInvoiceId)
  }

  const filterChips: FilterChip[] = facetChipSpecs.map((chip) => ({
    key: chip.key,
    label: chip.label,
    onRemove: () => handleFacetsChange(chip.withoutChip)
  }))

  return (
    <div className="space-y-6">
      <Card className="shadow-none border-border/80">
        <CardHeader>
          <CardTitle className="text-3xl font-serif text-primary">Fakturor</CardTitle>
          <CardDescription>Tabell med sökning och filtrering.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSearch}
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto]"
          >
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Sök på kund, kundnummer, personnummer eller fakturanummer"
            />
            <InvoiceFilterMenu
              filters={facetFilters}
              onChange={handleFacetsChange}
            />
            <PeriodMenu
              range={facetFilters.period}
              onChange={handlePeriodChange}
            />
            <SortMenu
              value={sort}
              options={INVOICE_SORT_OPTIONS}
              onChange={handleSortChange}
            />
            <Button type="submit">Sök</Button>
            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleReset}
              >
                Rensa
              </Button>
            )}
          </form>

          {hasFacetChips && (
            <div className="mt-3">
              <FilterChips chips={filterChips} />
            </div>
          )}
        </CardContent>
      </Card>

      {hasError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {!hasError && (
        <Card className="shadow-none border-border/80">
          <CardContent className="p-0">
            <InvoiceSummaryBar
              count={totalCount}
              summary={summary}
            />
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3 font-medium">Kund</th>
                    <th className="px-6 py-3 font-medium">Fakturanummer</th>
                    <th className="px-6 py-3 font-medium">Fakturadatum</th>
                    <th className="px-6 py-3 font-medium">Förfaller</th>
                    <th className="px-6 py-3 font-medium text-right">Belopp</th>
                    <th className="px-6 py-3 font-medium text-right">Återstår</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <InvoiceTableRow
                      key={invoice.id}
                      invoice={invoice}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {isLoading && <p className="px-6 py-8 text-sm text-muted-foreground">{LOADING_MESSAGE}</p>}
            {hasNoInvoices && <p className="px-6 py-8 text-sm text-muted-foreground">{emptyMessage}</p>}

            {showPagination && (
              <PaginationControls
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
